import React, { ReactNode } from "react";
import { Alert, Col, Container, Row, Spinner } from "react-bootstrap";
import styled from "styled-components";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

import { getDefaultDateRange } from "../../../utils/analytics";
import { AnalyticsFilters, AnalyticsFilterOptions } from "../../../types/analytics";
import AnalyticsFiltersComponent from "../../../components/analytics/AnalyticsFilters";
import { getAnalyticsWidget } from "../../../utils/Requests";
import Logger from "../../../utils/Logger";

type RecsDashboardProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type RecsDashboardState = {
    isLoading: boolean;
    error: string | null;
    filters: AnalyticsFilters;
    appliedFilters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    filterKey: number;
    recsRows: RecsRow[];
    recsDashRows: RecsApiRow[];
};

type RecsApiRow = Record<string, string | number | null | undefined>;

type RecsRow = {
    parameterRuleId: string;
    slotName: string;
    modelName: string;
    plays: number;
    playsPercentChange: number | null;
    sessionConversionPct: number | null;
    sessionConversionPctChange: number | null;
    playedBrands: number;
    playedBrandsPercentChange: number | null;
};

type RecsDashParsedRow = {
    brandRank: number;
    brandName: string;
    brandPlays: number;
    parameterRuleId: string;
    rulePlays: number;
    ruleSharePct: number | null;
};

type RecsDashChartPoint = {
    brandName: string;
    brandRank: number;
    brandPlays: number;
    [ruleId: string]: string | number;
};

const RESP: any = ResponsiveContainer as any;
const CCHART: any = ComposedChart as any;
const BR: any = Bar as any;
const LN: any = Line as any;
const XA: any = XAxis as any;
const YA: any = YAxis as any;
const CG: any = CartesianGrid as any;
const TT: any = Tooltip as any;
const LG: any = Legend as any;

const MetricGrid = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: nowrap;
    overflow-x: auto;
`;

const MetricCard = styled.div`
    border: 1px solid #343a40;
    border-radius: 8px;
    padding: 16px;
    background: #212529;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    min-width: 220px;
`;

const MetricLabel = styled.div`
    font-size: 0.9rem;
    color: #f8f9fa;
    opacity: 0.7;
    margin-bottom: 8px;
`;

const MetricValue = styled.div`
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.1;
    color: #f8f9fa;
`;

const MetricChange = styled.div<{ $positive: boolean; $negative: boolean }>`
    margin-top: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${props => props.$positive ? "#28a745" : props.$negative ? "#dc3545" : "#6c757d"};
`;

const RuleSection = styled.div`
    margin-top: 24px;
`;

const RuleHeader = styled.h5`
    margin-bottom: 10px;
`;

class RecsDashboard extends React.Component<RecsDashboardProps, RecsDashboardState> {
    constructor(props: RecsDashboardProps) {
        super(props);

        const { startDate, endDate } = getDefaultDateRange();

        const initialFilters: AnalyticsFilters = {
            startDate,
            endDate,
            attributionId: [],
            brandId: [],
            deviceType: []
        };

        this.state = {
            isLoading: true,
            error: null,
            filters: initialFilters,
            appliedFilters: initialFilters,
            filterOptions: {
                attributionIds: [],
                brandIds: [],
                deviceTypes: []
            },
            filterKey: 0,
            recsRows: [],
            recsDashRows: []
        };
    }

    componentDidMount(): void {
        this.fetchData();
    }

    private handleFilterChange = (filters: AnalyticsFilters): void => {
        this.setState({ filters });
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedFilters: { ...prevState.filters },
            filterKey: prevState.filterKey + 1
        }), () => {
            this.fetchData();
        });
    };

    private toNumber(value: number | string | undefined | null): number | null {
        if (value === null || value === undefined || value === "") {
            return null;
        }

        if (typeof value === "number") {
            return Number.isFinite(value) ? value : null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private formatChange(changePercent: number | null): string {
        if (changePercent === null || changePercent === undefined) {
            return "No change data";
        }

        const sign = changePercent > 0 ? "+" : "";
        return `${sign}${changePercent.toFixed(2)}%`;
    }

    private fetchData = async (): Promise<void> => {
        const { appliedFilters } = this.state;

        try {
            this.setState({ isLoading: true, error: null });

            const [recsResponse, recsDashResponse] = await Promise.all([
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "recs_plays",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.deviceType
                ),
                getAnalyticsWidget(
                    this.props.customerConfig,
                    "recs_attributions",
                    appliedFilters.startDate,
                    appliedFilters.endDate,
                    appliedFilters.attributionId,
                    appliedFilters.brandId,
                    appliedFilters.deviceType
                )
            ]);

            const rowsRaw = (recsResponse as any)?.result;

            const recsRows: RecsRow[] = (Array.isArray(rowsRaw) ? rowsRaw : [])
                .map((row: RecsApiRow) => {
                    const plays = this.toNumber(row["plays"]) ?? 0;
                    const parameterRuleId = String(row["parameters_rule_id"] || row["parameter_rule_id"] || "Unknown");

                    return {
                        parameterRuleId,
                        slotName: String(row["slot_name"] || ""),
                        modelName: String(row["model_name"] || ""),
                        plays,
                        playsPercentChange: this.toNumber(row["plays_percent_change"]),
                        sessionConversionPct: this.toNumber(row["session_conversion_pct"]),
                        sessionConversionPctChange: this.toNumber(row["session_conversion_pct_change"]),
                        playedBrands: this.toNumber(row["played_brands"]) ?? 0,
                        playedBrandsPercentChange: this.toNumber(row["played_brands_percent_change"])
                    };
                })
                .filter((row: RecsRow) => row.plays > 0)
                .sort((a: RecsRow, b: RecsRow) => b.plays - a.plays);

            const recsDashRaw = (recsDashResponse as any)?.result;
            const recsDashRows = Array.isArray(recsDashRaw) ? recsDashRaw as RecsApiRow[] : [];

            this.setState({ recsRows, recsDashRows });
        } catch (err) {
            Logger.error("Error fetching Recs dashboard data", err);
            this.setState({ error: "Failed to fetch Recs dashboard data" });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private renderRecsMetrics(row: RecsRow): ReactNode {
        const playsPositive = (row.playsPercentChange ?? 0) > 0;
        const playsNegative = (row.playsPercentChange ?? 0) < 0;

        const conversionPositive = (row.sessionConversionPctChange ?? 0) > 0;
        const conversionNegative = (row.sessionConversionPctChange ?? 0) < 0;

        const brandsPositive = (row.playedBrandsPercentChange ?? 0) > 0;
        const brandsNegative = (row.playedBrandsPercentChange ?? 0) < 0;

        return (
            <MetricGrid>
                <MetricCard>
                    <MetricLabel>Plays</MetricLabel>
                    <MetricValue>{row.plays.toLocaleString()}</MetricValue>
                    <MetricChange $positive={playsPositive} $negative={playsNegative}>
                        {this.formatChange(row.playsPercentChange)}
                    </MetricChange>
                </MetricCard>

                <MetricCard>
                    <MetricLabel>Conversion</MetricLabel>
                    <MetricValue>
                        {row.sessionConversionPct === null ? "-" : `${row.sessionConversionPct.toFixed(2)}%`}
                    </MetricValue>
                    <MetricChange $positive={conversionPositive} $negative={conversionNegative}>
                        {this.formatChange(row.sessionConversionPctChange)}
                    </MetricChange>
                </MetricCard>

                <MetricCard>
                    <MetricLabel>Brands</MetricLabel>
                    <MetricValue>{row.playedBrands.toLocaleString()}</MetricValue>
                    <MetricChange $positive={brandsPositive} $negative={brandsNegative}>
                        {this.formatChange(row.playedBrandsPercentChange)}
                    </MetricChange>
                </MetricCard>
            </MetricGrid>
        );
    }

    private parseRecsDashRows(): RecsDashParsedRow[] {
        const { recsDashRows } = this.state;

        return recsDashRows
            .map((row: RecsApiRow) => {
                const brandRank = this.toNumber(row["brand_rank"]) ?? 0;
                const brandName = String(row["brand_name"] || "Unknown");
                const brandPlays = this.toNumber(row["brand_plays"]) ?? 0;
                const parameterRuleId = String(row["parameters_rule_id"] || "Unknown");
                const rulePlays = this.toNumber(row["rule_plays"]) ?? 0;
                const ruleSharePct = this.toNumber(row["rule_share_pct"]);

                return {
                    brandRank,
                    brandName,
                    brandPlays,
                    parameterRuleId,
                    rulePlays,
                    ruleSharePct
                };
            })
            .filter((row: RecsDashParsedRow) => row.rulePlays > 0);
    }

    private formatRuleLabel(ruleId: string): string {
        if (ruleId === "Unattributed" || ruleId === "All attributed" || ruleId === "Unknown") {
            return ruleId;
        }

        return ruleId.length > 10 ? `${ruleId.slice(0, 8)}...` : ruleId;
    }

    private getRecsDashChartData(): { chartData: RecsDashChartPoint[] } {
        const parsedRows = this.parseRecsDashRows();

        const chartData = parsedRows
            .filter((row: RecsDashParsedRow) => row.parameterRuleId === "All attributed")
            .map((row: RecsDashParsedRow) => ({
                brandName: row.brandName,
                brandRank: row.brandRank,
                brandPlays: row.brandPlays,
                allAttributedShare: row.ruleSharePct ?? 0
            }))
            .sort((a, b) => a.brandRank - b.brandRank);

        return { chartData };
    }

    public render(): ReactNode {
        const { filters, filterOptions, isLoading, error, recsRows, recsDashRows } = this.state;
        const { chartData } = this.getRecsDashChartData();

        return (
            <div>
                <Container className="mw-100" key="recs-dashboard" style={{ position: "relative", paddingBottom: "40px" }}>
                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2>Recs Dashboard</h2>
                        </Col>
                    </Row>

                    <AnalyticsFiltersComponent
                        filters={filters}
                        filterOptions={filterOptions}
                        onFilterChange={this.handleFilterChange}
                        onApplyFilters={this.handleApplyFilters}
                        customerConfig={this.props.customerConfig}
                        enableGroupBy={{}}
                    />

                    {isLoading ? (
                        <Row className="justify-content-center">
                            <Col className="text-center" style={{ padding: "40px" }}>
                                <Spinner animation="border" variant="primary" />
                                <p style={{ marginTop: "10px" }}>Loading Recs dashboard...</p>
                            </Col>
                        </Row>
                    ) : error ? (
                        <Row>
                            <Col>
                                <Alert variant="danger">{error}</Alert>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            {recsRows.length === 0 ? (
                                <Row>
                                    <Col>
                                        <Alert variant="info">No rows with plays &gt; 0 for the selected date range.</Alert>
                                    </Col>
                                </Row>
                            ) : (
                                recsRows.map((row: RecsRow) => (
                                    <RuleSection key={`${row.parameterRuleId}-${row.slotName}-${row.modelName}`}>
                                        <RuleHeader>
                                            Rule: {row.parameterRuleId}
                                            {row.slotName ? ` • Slot: ${row.slotName}` : ""}
                                            {row.modelName ? ` • Model: ${row.modelName}` : ""}
                                        </RuleHeader>
                                        {this.renderRecsMetrics(row)}
                                    </RuleSection>
                                ))
                            )}

                            <RuleSection>
                                <RuleHeader>Recs Dash • Top Brands by Rule</RuleHeader>
                                {recsDashRows.length === 0 || chartData.length === 0 ? (
                                    <Alert variant="secondary" style={{ marginBottom: 0 }}>
                                        recs_attributions returned no "All attributed" rows for current filters.
                                    </Alert>
                                ) : (
                                    <div style={{ border: "1px solid #343a40", borderRadius: "8px", background: "#212529", padding: "12px" }}>
                                        <RESP width="100%" height={500}>
                                            <CCHART data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 70 }}>
                                                <CG strokeDasharray="3 3" stroke="#495057" />
                                                <XA dataKey="brandName" interval={0} angle={-35} textAnchor="end" height={90} tick={{ fill: "#f8f9fa", fontSize: 11 }} />
                                                <YA
                                                    yAxisId="share"
                                                    tick={{ fill: "#f8f9fa", fontSize: 12 }}
                                                    tickFormatter={(value: number) => `${value.toFixed(1)}%`}
                                                    domain={[0, 100]}
                                                />
                                                <YA
                                                    yAxisId="plays"
                                                    orientation="right"
                                                    tick={{ fill: "#f8f9fa", fontSize: 12 }}
                                                    tickFormatter={(value: number) => value.toLocaleString()}
                                                />
                                                <TT
                                                    formatter={(value: number, name: string) => {
                                                        if (name === "Brand plays") {
                                                            return [typeof value === "number" ? value.toLocaleString() : value, name];
                                                        }

                                                        return [`${Number(value).toFixed(2)}%`, name];
                                                    }}
                                                    contentStyle={{ backgroundColor: "#1b1f23", border: "1px solid #343a40", color: "#f8f9fa" }}
                                                    itemStyle={{ color: "#f8f9fa" }}
                                                    labelStyle={{ color: "#f8f9fa" }}
                                                />
                                                <LG />
                                                <BR
                                                    yAxisId="share"
                                                    dataKey="allAttributedShare"
                                                    fill="#5b84f1"
                                                    name="All attributed % share"
                                                />
                                                <LN
                                                    yAxisId="plays"
                                                    type="monotone"
                                                    dataKey="brandPlays"
                                                    stroke="#ff8c42"
                                                    strokeWidth={2}
                                                    dot={{ r: 2 }}
                                                    name="Brand plays"
                                                />
                                            </CCHART>
                                        </RESP>
                                    </div>
                                )}
                            </RuleSection>
                        </>
                    )}
                </Container>
            </div>
        );
    }
}

export default RecsDashboard;